<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import CartWidgetGroupV2 from "@retailer/svelte/CartWidget/v2/CartWidgetGroupV2.svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import { CartWidgetTargetObjectBase } from "@shared/services/event_tracking/entities";
  import {
    sendCartTrackingEvents,
    sendClickTracking,
  } from "@retailer/svelte/CartWidget/v2/Services/CartEventTracking";
  import {
    Notice,
    StockLevels,
  } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import CartWidgetNotice from "@retailer/svelte/CartWidget/v2/CartWidgetNotice.svelte";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import type { Distributor } from "@shared/types/Distributor";
  import * as PickupWarehousesService from "@retailer/services/pickup_warehouses_service";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { pickupWarehousesStore } from "@retailer/svelte/stores/PickupWarehousesStore";
  import PickupLocationModal from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupLocationModal.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import RequestItem from "@retailer/svelte/CartWidget/v2/middleware/RequestItem.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";
  import type { ProductLine, Product } from "@shared/types/ProductLine";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";
  import { ProductCardSkuItemStockStatusEntity } from "@shared/services/event_tracking/entities";

  export let inventoryId: number;
  export let productId: number;
  export let productLineId: number = null;
  export let productLine: ProductLine = null;
  export let productInventoryData: Product[] = [];
  export let distributor: Partial<Distributor>;
  export let trackingEntities: BaseEntityClass[] = [];
  export let variant: "grid" | "row" = "row";
  export let targetObjectTypeBase: CartWidgetTargetObjectBase =
    CartWidgetTargetObjectBase.ProductListItem;
  export let trackingEntity: ProductCardSkuItemStockStatusEntity = null;

  const hasFullfillmentMethodsRollout = stagedRolloutCheck("marketplace__fulfillment_methods");

  const { trackClick, trackMrcEvents } = EventTracking;

  const dispatch = createEventDispatcher();

  const trackCartChangeEvent = event => {
    sendCartTrackingEvents(event, trackingEntities, inventoryId);
  };

  const trackCartInteractionEvent = event => {
    sendClickTracking(targetObjectTypeBase, event, trackingEntities);
  };

  let showPickupSelectionModal = false;

  let onPickupClick = async () => {
    trackClick("check_pickup_availability", trackingEntities);
    await PickupWarehousesService.get(productId, distributor.id);
    showPickupSelectionModal = true;
  };

  const closeModal = () => {
    showPickupSelectionModal = false;
  };

  async function onPickupSelection(selectedPickupLocation) {
    const quantityToAdd = 1;
    const unitQuantity = cartWidgetItem.sold_by.includes("units") ? quantityToAdd : 0;
    const caseQuantity = unitQuantity ? 0 : quantityToAdd;

    await InventoryItemStore.addToCart(inventoryId, {
      inventory_id: inventoryId,
      unit_quantity: unitQuantity,
      case_quantity: caseQuantity,
      warehouse_id: selectedPickupLocation.id,
    });
    await CartStore.load();

    closeModal();
  }

  function openFulfillmentMethodsDrawer(): void {
    if (distributor)
      dispatch("openDrawer", {
        distributor,
        productLine,
        products: productInventoryData,
        sku: cartWidgetItem?.sku,
      });
  }

  let loaded: boolean = false;

  onMount(async () => {
    loaded = false;
    const response = await InventoryItemStore.fetchInventoryItem(inventoryId);
    loaded = response?.loaded;
  });

  let fulfillmentMethod: RetailerFulfillmentMethod;
  $: {
    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        distributor?.id,
        distributor?.uses_warehouse_pickup,
        hasFullfillmentMethodsRollout && distributor?.uses_wine_cellar,
      ) &&
      !hideFulfillmentMethodPickerForDistributor(distributor?.id)
    ) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributor?.id] || null;
      InventoryItemStore.fetchInventoryItem(inventoryId);
    }
  }
  $: fulfillmentWarehouseId =
    fulfillmentMethod?.warehouse_id || cartWidgetItem?.distributor_summary?.default_warehouse_id;
  $: cartWidgetItem = $InventoryItemStore?.inventoryItems[inventoryId];
  $: informationalNotices = cartWidgetItem?.notices.filter((notice: Notice) => {
    return notice.level === "info" && notice.message;
  });
  $: notices = cartWidgetItem?.notices.filter(notice => notice.level !== "info" && notice.message);
  $: pickUpOptionsAvailable =
    cartWidgetItem?.stock_info?.additional_action?.endsWith("pickup_available");
  $: isOutOfStock = cartWidgetItem?.stock_info?.level == StockLevels.outOfStock;
  $: showOtherAvailableLocations = pickUpOptionsAvailable && !cartWidgetItem?.fulfillment_summary;
  $: needsAccountNumber =
    cartWidgetItem?.stock_info?.additional_action?.endsWith("connect_account");
  $: stockStatus = cartWidgetItem?.stock_info?.level;
  $: {
    if (stockStatus && (!trackingEntity || stockStatus !== trackingEntity.stock_status)) {
      trackingEntity = new ProductCardSkuItemStockStatusEntity({
        inventory_id: inventoryId,
        stock_status: stockStatus,
        product_id: productId,
        distributor_id: distributor?.id,
      });
    }
  }
</script>

<style>
  .grid-styles {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }
</style>

{#if loaded && cartWidgetItem}
  <RequestItem {cartWidgetItem} {fulfillmentWarehouseId}>
    <div
      class="fizz-flow-8"
      class:grid-styles={variant === "grid"}
      slot="cart-widget"
      use:trackMrcEvents={[trackingEntity ? [trackingEntity] : []]}
    >
      {#each notices as notice}
        <CartWidgetNotice {notice} />
      {/each}
      <CartWidgetGroupV2
        {fulfillmentMethod}
        {fulfillmentWarehouseId}
        widgetItem={cartWidgetItem}
        showFulfillmentIndicator={true}
        tooltipPosition={variant === "row" && $LayoutStore.isDesktop
          ? "fizz-tooltip-right"
          : "fizz-tooltip-left"}
        tooltipMaxWidth={variant === "row" ? "300px" : "200px"}
        on:cartWidgetChange={trackCartChangeEvent}
        on:updateQuantity={trackCartInteractionEvent}
      />
      {#if showOtherAvailableLocations}
        <div class="fizz-stack-8">
          {#if hasFullfillmentMethodsRollout && cartWidgetItem?.distributor_summary?.fulfillment_model === "pickup_only"}
            <button
              on:click|preventDefault={cartWidgetItem?.distributor_summary?.uses_wine_cellar
                ? openFulfillmentMethodsDrawer
                : onPickupClick}
              class="fizz-button fizz-full-width fizz-stack-8"
            >
              Check other locations
            </button>
          {:else if !hasFullfillmentMethodsRollout || (hasFullfillmentMethodsRollout && cartWidgetItem?.distributor_summary?.fulfillment_model === "pickup_and_delivery" && !needsAccountNumber)}
            <button
              on:click|preventDefault={hasFullfillmentMethodsRollout &&
              cartWidgetItem?.distributor_summary?.uses_wine_cellar
                ? openFulfillmentMethodsDrawer
                : onPickupClick}
              class="fizz-button fizz-full-width fizz-stack-8"
            >
              View more availability
            </button>
          {/if}
        </div>
      {/if}
      {#if isOutOfStock && productLineId && !cartWidgetItem.buying_options.length}
        <a class="fizz-button fizz-full-width" href="/products/{productLineId}">
          See other sizes
        </a>
      {/if}
      {#each informationalNotices as notice}
        <CartWidgetNotice {notice} />
      {/each}
    </div>
  </RequestItem>
{:else}
  <div class="fizz-flex-row-4">
    <span class="fizz-icon-spinner-small" />
    <p>Loading...</p>
  </div>
{/if}
{#if showPickupSelectionModal && $pickupWarehousesStore.loaded}
  <PickupLocationModal
    selectedPickupLocationId={null}
    pickupOptions={$pickupWarehousesStore.pickupWarehouses}
    {distributor}
    onCancel={closeModal}
    {onPickupSelection}
    shouldShowLowStock={pickUpOptionsAvailable}
  />
{/if}
